import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  apiErrorSchema,
  loginResponse,
  SESSION_ACCOUNT_DEACTIVATED,
  SESSION_ENDED_ELSEWHERE,
  type Me,
} from '@cms/shared';
import { apiRequest, setAccessToken, setRefreshHandler } from './apiClient.js';
import { queryClient } from './queryClient.js';
import { startSessionPoll, type SessionEndReason } from './sessionPoll.js';

/**
 * Session state for the SPA (§6.5).
 *
 * The access token lives in this module's closure and nowhere else — not localStorage,
 * not sessionStorage, not a cookie the JS can read. An XSS payload can still *use* the
 * running app, but it cannot lift a token and keep using it after the tab closes.
 *
 * Continuity across a reload comes from the refresh cookie instead: on mount the app asks
 * the server to rotate it, and either gets a fresh session or learns it is anonymous.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

/**
 * Options on a login, which is currently one answer to one question.
 *
 * An object rather than a third positional boolean: `login(email, password, true)` reads as nothing
 * at the call site, and this is a flag whose meaning is "end somebody else's session".
 */
export interface LoginOptions {
  /** The user has been shown the prompt and said to replace the other session (§5, step 4). */
  replaceExistingSession?: boolean;
}

interface AuthContextValue {
  status: AuthStatus;
  user: Me | null;
  login: (email: string, password: string, options?: LoginOptions) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Changing your own password, and staying signed in through it. The server revokes every
   * session and issues a fresh one, so the new token replaces the old in the same call.
   */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /**
   * True when this session was ended by a login somewhere else, until the login page has said so
   * (docs/SINGLE-SESSION-PLAN.md §4a).
   *
   * On the context rather than in a route param or a toast: `RequireAuth` already sends an anonymous
   * user to /login, and a query string would survive a bookmark and explain a sign-out that never
   * happened. This is state about the session that just ended, and it lives with the session.
   */
  endedElsewhere: boolean;
  /**
   * Why the last session ended, when the person needs telling: signed in elsewhere, or the account
   * was deactivated (OCC-22). `endedElsewhere` is the first of these, kept for its readers.
   */
  endedReason: SessionEndReason | null;
  /** Called by whatever displayed the message, so a later ordinary sign-out is not explained wrongly. */
  acknowledgeEndedElsewhere: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * What a refresh attempt concluded.
 *
 * ── Why "failed" is no longer enough ──────────────────────────────────────────────────
 * Because two failures mean different things to the person at the keyboard. An expired or missing
 * cookie is "your session ran out, sign in again" and needs no explanation. A session ended by a
 * login somewhere else is the one case where the app looks broken — work in progress, a page that
 * stops responding, and no reason given — and it is the case this slice exists for
 * (docs/SINGLE-SESSION-PLAN.md §4a).
 *
 * So the outcome carries the reason, and `null` is not the return value any more. Collapsing these
 * two into a falsy check is exactly how the message gets lost.
 */
type RefreshOutcome = { user: Me } | { user: null; ended: SessionEndReason | null };

/**
 * Raw fetch rather than apiRequest: this *is* the refresh path, and going through the
 * client's 401 handling would have it call itself.
 */
async function refreshSession(): Promise<RefreshOutcome> {
  const response = await fetch('/api/v1/auth/refresh', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) return { user: null, ended: await endReasonOf(response) };

  const parsed = loginResponse.safeParse(await response.json());
  if (!parsed.success) return { user: null, ended: null };

  setAccessToken(parsed.data.accessToken);
  return { user: parsed.data.user };
}

/**
 * Reads the reason off a failed refresh, through the shared error contract.
 *
 * Every step here can fail on a response that is not the one expected — a proxy's HTML error page, a
 * truncated body — and none of those should throw inside the refresh path, because a thrown error
 * there is a session that never resolves. Anything unrecognisable is "not ended elsewhere", which
 * degrades to the ordinary expiry handling rather than to a wrong explanation.
 */
async function endReasonOf(response: Response): Promise<SessionEndReason | null> {
  try {
    const parsed = apiErrorSchema.safeParse(await response.json());
    if (!parsed.success) return null;
    const details = parsed.data.error.details as { reason?: unknown } | undefined;
    if (details?.reason === SESSION_ENDED_ELSEWHERE) return 'elsewhere';
    if (details?.reason === SESSION_ACCOUNT_DEACTIVATED) return 'deactivated';
    return null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<Me | null>(null);
  const [endedReason, setEndedReason] = useState<SessionEndReason | null>(null);
  const endedElsewhere = endedReason === 'elsewhere';

  /**
   * Everything that has to be true once this session is over, in one place.
   *
   * Declared before the effects that call it so the order is obvious rather than relying on when
   * effect callbacks happen to run.
   *
   * The cache is cleared for the same reason `logout` clears it: the data in it belongs to a session
   * that no longer exists, and leaving it there means the next person to sign in on this browser
   * sees the previous one's records flash past before fresh queries land. That was already true of
   * an ordinary expiry; it is more obviously wrong when the session was ended deliberately.
   */
  const endSession = useCallback((reason: SessionEndReason | null) => {
    setAccessToken(null);
    setUser(null);
    setStatus('anonymous');
    setEndedReason(reason);
    queryClient.clear();
  }, []);

  // Teaches the API client how to recover from a 401. Registered once, before any query
  // runs, so an expired access token is a transparent retry rather than a logout.
  useEffect(() => {
    setRefreshHandler(async () => {
      const outcome = await refreshSession();
      if (outcome.user) {
        setUser(outcome.user);
        setStatus('authenticated');
        return true;
      }
      // The refresh cookie is gone, expired, or was revoked — including the reuse-detection
      // case, where the server has deliberately killed the family.
      endSession(outcome.ended);
      return false;
    });
  }, [endSession]);

  // Silent refresh on load: a reload should not look like a logout.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const outcome = await refreshSession();
      if (cancelled) return;

      if (outcome.user) {
        setUser(outcome.user);
        setStatus('authenticated');
        return;
      }

      /**
       * The closed-laptop case, and the reason the bootstrap needs this too.
       *
       * Somebody signs in on a phone, comes back to the desktop tab hours later and reloads. There
       * is no live session to interrupt — this is a cold start — and without the reason it reads as
       * an ordinary expiry, which is an explanation that happens to be wrong.
       */
      endSession(outcome.ended);
    })();

    return () => {
      cancelled = true;
    };
  }, [endSession]);

  /**
   * The thirty-second poll, while there is a session to lose (docs/SINGLE-SESSION-PLAN.md §4c).
   *
   * Only while authenticated: an anonymous tab sitting on the login page has nothing to be told, and
   * polling there would be a request every thirty seconds for the life of the page.
   *
   * `endSession(reason)` and nothing else — the redirect and the message already exist. `RequireAuth`
   * sends an anonymous user to /login, and slice 5's notice reads the same flag whether it was set
   * here or by a failed refresh, which is what "the same sentence in every path" bought.
   *
   * Access changed (a role assigned, removed, edited or deactivated): refresh now, so the menus and
   * the API agree with the new permissions within one interval. A refresh that fails ends the session
   * the ordinary way.
   */
  useEffect(() => {
    if (status !== 'authenticated') return;
    return startSessionPoll(endSession, undefined, async () => {
      const outcome = await refreshSession();
      if (outcome.user) setUser(outcome.user);
      else endSession(outcome.ended);
    });
  }, [status, endSession]);

  const login = useCallback(async (email: string, password: string, options?: LoginOptions) => {
    const result = await apiRequest('/auth/login', loginResponse, {
      method: 'POST',
      /**
       * Sent only when the user has actually said yes, never as a default.
       *
       * Absent means "ask me", which is the conservative answer: a client that always sent it would
       * silently end somebody's session on every sign-in, and the person whose session ended would
       * never have been asked.
       */
      body: {
        email,
        password,
        ...(options?.replaceExistingSession === true ? { confirmReplaceSession: true } : {}),
      },
    });

    setAccessToken(result.accessToken);
    setUser(result.user);
    setStatus('authenticated');
    // Signing in spends the message: it explained the sign-out that led here, and leaving it set
    // would explain the next one too.
    setEndedReason(null);
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const result = await apiRequest('/auth/change-password', loginResponse, {
      method: 'POST',
      body: { currentPassword, newPassword },
    });

    // Same shape as login: the old access token was just revoked along with every other
    // session, so holding on to it would sign this tab out a moment later.
    setAccessToken(result.accessToken);
    setUser(result.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } catch {
      // Deliberately swallowed, not rethrown: signing out must always succeed from the
      // caller's point of view, and a rejected promise here would surface as an unhandled
      // rejection at every call site. The cost is that a network failure leaves the server
      // session alive until it expires — the local session is gone either way, and the
      // refresh cookie is cleared on the next successful logout or by its own expiry.
    } finally {
      // Local state is cleared whatever the server said: a failed logout call must not
      // leave someone looking logged in.
      // `false`: an ordinary sign-out is not a session ended somewhere else, and telling somebody
      // who clicked Sign out that they signed in elsewhere is a lie the login page would repeat.
      endSession(null);
    }
  }, [endSession]);

  const acknowledgeEndedElsewhere = useCallback(() => setEndedReason(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      login,
      logout,
      changePassword,
      endedElsewhere,
      endedReason,
      acknowledgeEndedElsewhere,
    }),
    [
      status,
      user,
      login,
      logout,
      changePassword,
      endedElsewhere,
      endedReason,
      acknowledgeEndedElsewhere,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/**
 * Who is signed in, for code that needs an identity and not a session.
 *
 * ── Why this does not throw where `useAuth` does ──────────────────────────────────────
 * Because the question is different. `useAuth` is the session contract — sign in, sign out, change a
 * password — and a component reaching for it outside the provider is a wiring mistake worth failing loudly
 * on. This answers "whose preference is this", and outside a provider the honest answer is "nobody in
 * particular": a per-user key that falls back to a name nothing else uses is correct behaviour, not a bug
 * to crash over.
 *
 * Returning a string rather than `string | null` for the same reason — every caller would otherwise write
 * the same fallback, and the one that forgot would key everybody's preference under `undefined`.
 */
export function useCurrentUserId(): string {
  return useContext(AuthContext)?.user?.id ?? 'anonymous';
}
